import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { DataTable } from "@/components/app/patterns/data-table";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LogIn, LogOut } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { useZoneFor } from "@/lib/facility/zone";
import { outingColumns } from "@/routes/_authenticated/_visitors/visitors/-components/outing-columns";
import { OutingSignInDialog } from "@/routes/_authenticated/_visitors/visitors/-components/outing-sign-in-dialog";
import { OutingSignOutDialog } from "@/routes/_authenticated/_visitors/visitors/-components/outing-sign-out-dialog";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/outings")({
  component: ResidentOutingsTab,
});

const LIMIT = 100;

/** Every time this resident left the building and came back, from the kiosk, Grace or staff. */
function ResidentOutingsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canManage = useCan("visitor:manage", resident.facilityId);
  const zoneFor = useZoneFor();
  const [dialog, setDialog] = useState<"sign-out" | "sign-in" | null>(null);
  const list = useQuery(
    orpc.visitors.outings.list.queryOptions({
      input: { residentId, status: "all", limit: LIMIT, offset: 0 },
    }),
  );
  const rows = list.data?.rows ?? [];
  const away = rows.some((row) => !row.returnedAt);
  const name = `${resident.firstName} ${resident.lastName}`;
  const facilityId = resident.facilityId;

  return (
    <div className="flex flex-col gap-4">
      {canManage && facilityId && list.isSuccess ? (
        <div className="flex justify-end">
          {away ? (
            <Button onClick={() => setDialog("sign-in")}>
              <LogIn data-icon="inline-start" aria-hidden="true" />
              Sign back in
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setDialog("sign-out")}>
              <LogOut data-icon="inline-start" aria-hidden="true" />
              Sign out
            </Button>
          )}
        </div>
      ) : null}
      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load this resident's outings."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={outingColumns(zoneFor, false)}
          noun="outings"
          rowId={(row) => row.id}
          pending={list.isPending}
          empty={
            <EmptyState
              title="No outings recorded"
              description="Outings appear here when the resident signs out at the kiosk or with Grace, or when staff sign them out."
            />
          }
        />
      )}
      {dialog === "sign-out" && facilityId ? (
        <OutingSignOutDialog
          facilityId={facilityId}
          resident={{ id: residentId, label: name }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "sign-in" && facilityId ? (
        <OutingSignInDialog
          residentId={residentId}
          residentName={name}
          facilityId={facilityId}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
